import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { Check, Upload, FileText } from "lucide-react";

const STEPS = ["Book Details", "Manuscript", "Cover", "Plan & Payment"];
const inputCls = "mt-1 w-full bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 focus:border-neutral-900 outline-none";

export default function NewProject() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const existingId = params.get("id");
  const [step, setStep] = useState(0);
  const [projectId, setProjectId] = useState(existingId || null);
  const [meta, setMeta] = useState({ title: "", subtitle: "", description: "", keywords: "", categories: "", genre: "", language: "English", target_price: "" });
  const [files, setFiles] = useState([]);
  const [coverMode, setCoverMode] = useState("upload");
  const [coverBrief, setCoverBrief] = useState("");
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data));
    if (existingId) {
      api.get(`/projects/${existingId}`).then((r) => {
        const p = r.data;
        setMeta({ title: p.title, subtitle: p.subtitle || "", description: p.description || "",
          keywords: (p.keywords || []).join(", "), categories: (p.categories || []).join(", "),
          genre: p.genre || "", language: p.language || "English", target_price: p.target_price ?? "" });
        setCoverBrief(p.cover_brief || "");
        refreshFiles(existingId);
      });
    }
  }, [existingId]);

  const refreshFiles = (id) => api.get(`/projects/${id}/files`).then((r) => setFiles(r.data)).catch(() => {});

  const metaPayload = () => ({
    ...meta,
    keywords: meta.keywords.split(",").map((s) => s.trim()).filter(Boolean),
    categories: meta.categories.split(",").map((s) => s.trim()).filter(Boolean),
    target_price: meta.target_price === "" ? null : parseFloat(meta.target_price),
  });

  const saveMeta = async (goNext) => {
    if (!meta.title.trim()) return toast.error("Title is required");
    setBusy(true);
    try {
      if (projectId) {
        await api.put(`/projects/${projectId}`, metaPayload());
      } else {
        const { data } = await api.post("/projects", metaPayload());
        setProjectId(data.id);
      }
      toast.success("Draft saved");
      if (goNext) setStep(1);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  const uploadFile = async (file, endpoint) => {
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/${endpoint}`, fd);
      toast.success(`${file.name} uploaded`);
      refreshFiles(projectId);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  const saveBrief = async () => {
    setBusy(true);
    try {
      await api.put(`/projects/${projectId}/cover-brief`, { cover_brief: coverBrief });
      toast.success("Cover brief saved");
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  const pay = async () => {
    if (!selectedPlan) return toast.error("Select a plan first");
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        project_id: projectId, plan_id: selectedPlan, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(fmtErr(e)); setBusy(false); }
  };

  const manuscripts = files.filter((f) => f.kind === "manuscript");
  const covers = files.filter((f) => f.kind === "cover");
  const set = (k) => (e) => setMeta((m) => ({ ...m, [k]: e.target.value }));

  return (
    <div className="max-w-3xl mx-auto" data-testid="new-project-wizard">
      <h1 className="text-3xl font-bold tracking-tight text-neutral-900 font-heading mb-2">New Book Submission</h1>
      <p className="text-neutral-500 mb-8">Your progress is saved as a draft at every step.</p>

      <div className="flex items-center gap-2 mb-10">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2 flex-1">
            <button
              data-testid={`wizard-step-${i}`}
              onClick={() => { if (projectId || i === 0) setStep(i); }}
              className={`flex items-center gap-2 text-sm font-medium ${i === step ? "text-neutral-900" : i < step ? "text-emerald-700" : "text-neutral-400"}`}>
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs border ${i === step ? "bg-neutral-900 text-white border-neutral-900" : i < step ? "bg-emerald-50 border-emerald-300" : "border-neutral-300"}`}>
                {i < step ? <Check size={12} /> : i + 1}
              </span>
              <span className="hidden sm:inline">{s}</span>
            </button>
            {i < STEPS.length - 1 && <div className="h-px bg-neutral-200 flex-1" />}
          </div>
        ))}
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg p-8 shadow-sm">
        {step === 0 && (
          <div className="space-y-4">
            <div><label className="text-sm font-medium text-neutral-700">Title *</label>
              <input data-testid="wizard-title-input" value={meta.title} onChange={set("title")} className={inputCls} /></div>
            <div><label className="text-sm font-medium text-neutral-700">Subtitle</label>
              <input data-testid="wizard-subtitle-input" value={meta.subtitle} onChange={set("subtitle")} className={inputCls} /></div>
            <div><label className="text-sm font-medium text-neutral-700">Description</label>
              <textarea data-testid="wizard-description-input" rows={4} value={meta.description} onChange={set("description")} className={inputCls} /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="text-sm font-medium text-neutral-700">Genre</label>
                <input data-testid="wizard-genre-input" value={meta.genre} onChange={set("genre")} className={inputCls} placeholder="e.g. Thriller" /></div>
              <div><label className="text-sm font-medium text-neutral-700">Language</label>
                <input value={meta.language} onChange={set("language")} className={inputCls} /></div>
            </div>
            <div><label className="text-sm font-medium text-neutral-700">Keywords (comma-separated)</label>
              <input data-testid="wizard-keywords-input" value={meta.keywords} onChange={set("keywords")} className={inputCls} placeholder="mystery, detective, noir" /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="text-sm font-medium text-neutral-700">Categories (comma-separated)</label>
                <input value={meta.categories} onChange={set("categories")} className={inputCls} placeholder="Fiction > Mystery" /></div>
              <div><label className="text-sm font-medium text-neutral-700">Target price (USD)</label>
                <input type="number" step="0.01" value={meta.target_price} onChange={set("target_price")} className={inputCls} placeholder="9.99" /></div>
            </div>
            <div className="flex justify-between pt-4">
              <button data-testid="wizard-save-draft" onClick={() => saveMeta(false)} disabled={busy}
                className="px-4 py-2 border border-neutral-300 rounded-md text-sm font-medium text-neutral-700 hover:bg-neutral-50">Save draft</button>
              <button data-testid="wizard-next-0" onClick={() => saveMeta(true)} disabled={busy}
                className="px-5 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">Save & continue</button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div>
            <h3 className="font-semibold font-heading text-lg mb-1">Upload your manuscript</h3>
            <p className="text-sm text-neutral-500 mb-6">Accepted formats: .docx, .pdf, .epub — new uploads become new versions.</p>
            <label className="border-2 border-dashed border-neutral-300 rounded-lg p-10 flex flex-col items-center justify-center cursor-pointer hover:border-neutral-900 transition-colors">
              <Upload size={28} className="text-neutral-400 mb-3" />
              <span className="text-sm font-medium text-neutral-700">Click to choose a file</span>
              <input data-testid="manuscript-file-input" type="file" accept=".docx,.pdf,.epub" className="hidden"
                onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0], "manuscript")} />
            </label>
            {manuscripts.length > 0 && (
              <ul className="mt-5 space-y-2">
                {manuscripts.map((f) => (
                  <li key={f.id} data-testid={`manuscript-file-${f.version}`} className="flex items-center gap-3 text-sm bg-neutral-50 border border-neutral-200 rounded-md px-4 py-2.5">
                    <FileText size={16} className="text-neutral-500" />
                    <span className="font-medium text-neutral-800">{f.filename}</span>
                    <span className="text-xs text-neutral-400 ml-auto">v{f.version} · {(f.size / 1024).toFixed(0)} KB</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex justify-between pt-6">
              <button onClick={() => setStep(0)} className="px-4 py-2 border border-neutral-300 rounded-md text-sm font-medium text-neutral-700 hover:bg-neutral-50">Back</button>
              <button data-testid="wizard-next-1" onClick={() => setStep(2)} className="px-5 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">Continue</button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <h3 className="font-semibold font-heading text-lg mb-4">Cover</h3>
            <div className="flex gap-2 mb-6">
              {["upload", "brief"].map((m) => (
                <button key={m} data-testid={`cover-mode-${m}`} onClick={() => setCoverMode(m)}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium border ${coverMode === m ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-300 text-neutral-600 hover:bg-neutral-50"}`}>
                  {m === "upload" ? "I have a cover" : "Brief our designers (Professional & Premium)"}
                </button>
              ))}
            </div>
            {coverMode === "upload" ? (
              <>
                <label className="border-2 border-dashed border-neutral-300 rounded-lg p-10 flex flex-col items-center justify-center cursor-pointer hover:border-neutral-900 transition-colors">
                  <Upload size={28} className="text-neutral-400 mb-3" />
                  <span className="text-sm font-medium text-neutral-700">Upload cover (.jpg, .png, .pdf) — multiple sizes welcome</span>
                  <input data-testid="cover-file-input" type="file" accept=".jpg,.jpeg,.png,.pdf,.webp" className="hidden"
                    onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0], "cover")} />
                </label>
                {covers.length > 0 && (
                  <ul className="mt-5 space-y-2">
                    {covers.map((f) => (
                      <li key={f.id} className="flex items-center gap-3 text-sm bg-neutral-50 border border-neutral-200 rounded-md px-4 py-2.5">
                        <FileText size={16} className="text-neutral-500" />
                        <span className="font-medium">{f.filename}</span>
                        <span className="text-xs text-neutral-400 ml-auto">v{f.version}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : (
              <div>
                <textarea data-testid="cover-brief-input" rows={5} value={coverBrief} onChange={(e) => setCoverBrief(e.target.value)}
                  className={inputCls} placeholder="Describe the mood, imagery, colors, and comparable covers you like..." />
                <button data-testid="save-cover-brief" onClick={saveBrief} disabled={busy}
                  className="mt-3 px-4 py-2 border border-neutral-300 rounded-md text-sm font-medium text-neutral-700 hover:bg-neutral-50">Save brief</button>
              </div>
            )}
            <div className="flex justify-between pt-6">
              <button onClick={() => setStep(1)} className="px-4 py-2 border border-neutral-300 rounded-md text-sm font-medium text-neutral-700 hover:bg-neutral-50">Back</button>
              <button data-testid="wizard-next-2" onClick={() => setStep(3)} className="px-5 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">Continue</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <h3 className="font-semibold font-heading text-lg mb-1">Choose your publishing plan</h3>
            <p className="text-sm text-neutral-500 mb-6">Payment locks your submission and moves it into our production pipeline.</p>
            {manuscripts.length === 0 && (
              <div className="text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 text-sm mb-4">
                Heads up: you haven't uploaded a manuscript yet. You can still pay now and upload before we begin review — but we recommend uploading first.
              </div>
            )}
            <div className="grid sm:grid-cols-3 gap-4">
              {plans.filter((p) => !p.custom).map((p) => (
                <button key={p.id} data-testid={`select-plan-${p.id}`} onClick={() => setSelectedPlan(p.id)}
                  className={`text-left border rounded-lg p-5 transition-colors ${selectedPlan === p.id ? "border-neutral-900 ring-2 ring-neutral-900" : "border-neutral-200 hover:border-neutral-400"}`}>
                  <p className="font-semibold font-heading">{p.name}</p>
                  <p className="text-2xl font-bold font-heading mt-1">${p.price}</p>
                  {p.monthly_price != null && <p className="text-[11px] text-neutral-500">then ${p.monthly_price}/mo membership · first 30 days included</p>}
                  <p className="text-xs text-neutral-500 mt-2">{p.description}</p>
                </button>
              ))}
            </div>
            <div className="flex justify-between pt-6">
              <button onClick={() => setStep(2)} className="px-4 py-2 border border-neutral-300 rounded-md text-sm font-medium text-neutral-700 hover:bg-neutral-50">Back</button>
              <div className="flex gap-3">
                <Link to={projectId ? `/projects/${projectId}` : "/dashboard"} className="px-4 py-2 border border-neutral-300 rounded-md text-sm font-medium text-neutral-700 hover:bg-neutral-50">Finish later</Link>
                <button data-testid="pay-button" onClick={pay} disabled={busy || !selectedPlan}
                  className="px-5 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium disabled:opacity-50">
                  {busy ? "Redirecting..." : "Pay & submit"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
